import React, { useEffect, useState } from "react";
import { api, statusColor } from "../lib/api";
import { Link } from "react-router-dom";
import { ArchiveRestore, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function Archive() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/jobs", { params: { archived: true } });
      setJobs(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const unarchive = async (id) => {
    await api.patch(`/jobs/${id}`, { archived: false });
    toast.success("Restored");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Permanently delete this job?")) return;
    await api.delete(`/jobs/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-6" data-testid="archive-page">
      <div>
        <div className="label-tiny">Archive</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2">Archived jobs</h1>
        <p className="text-zinc-600 mt-2">Jobs you've put away. Restore them or delete forever.</p>
      </div>

      {loading ? (
        <div className="space-y-2">{["a","b","c"].map(k => <div key={k} className="h-20 bg-zinc-100" />)}</div>
      ) : jobs.length === 0 ? (
        <div className="brutal-card p-10 text-center">
          <div className="label-tiny">Archive is empty</div>
          <p className="mt-3 text-zinc-600">Rejected jobs and ones you archive manually will land here.</p>
        </div>
      ) : (
        <div className="border border-zinc-200">
          {jobs.map((j) => (
            <div key={j.job_id} className="flex items-center justify-between gap-3 p-4 border-b border-zinc-200 last:border-b-0 hover:bg-zinc-50" data-testid={`archived-row-${j.job_id}`}>
              <Link to={`/jobs/${j.job_id}`} className="min-w-0 flex-1">
                <div className="font-medium truncate">{j.title}</div>
                <div className="text-sm text-zinc-500 truncate">{j.company}{j.location ? ` · ${j.location}` : ""}</div>
              </Link>
              <div className="flex items-center gap-2 shrink-0">
                <span className="hidden sm:flex items-center text-xs">
                  <span className="status-dot" style={{ backgroundColor: statusColor(j.status) }} />
                  <span className="font-mono-jb uppercase tracking-wider">{j.status}</span>
                </span>
                <Button variant="outline" onClick={() => unarchive(j.job_id)} className="rounded-none border-zinc-300 h-9" data-testid={`unarchive-${j.job_id}`}>
                  <ArchiveRestore size={14} className="mr-1.5" /> Restore
                </Button>
                <Button variant="ghost" onClick={() => remove(j.job_id)} className="rounded-none text-red-600 hover:text-red-700 hover:bg-red-50 h-9" data-testid={`delete-${j.job_id}`}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
